import { useRef, useState, lazy, Suspense, useEffect } from 'react'
import styles from './TileCarousel.module.scss'
import { NEXT_IMAGE_QUALITY, TWENTY_FOUR_HOURS_IN_MS } from '~constants'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { Heading, PlayButton, PauseButton, PortalModal } from '~elements'
import { DetailCard, EventModalContent as EventModalContentType } from '~types'
import { useWindowSize, useIsSmartphoneLandscape } from 'utils/hooks'
import DetailsCard from '../DetailsCard/DetailsCard'
import { fireAnalyticsEvent, getGtmLinkPosition } from '~analyticsUtils'
// @ts-ignore
import Slider from 'react-slick'
import Image from 'next/image'
import { isMobileOnly } from 'react-device-detect'
import EventModalContent from '../EventModalContent/EventModalContent'
import { constructS3Url, preloadImageByUrl } from 'utils'
import { fetchVimeoVideo, getVimeoVideoByRendition, getVimeoPictureByResolution } from 'lib/vimeo'
import { useRouter } from 'next/router'

const CarouselVideo = lazy(() => import('components/elements/CarouselVideo/CarouselVideo'))

interface IUrl {
  url: string
}

interface TileCarouselProps {
  items: {
    image: {
      url: string
    }
    imageUrlS3: string
    text: string
  }[]
  onTileClick?: (index: number) => void
  loopCarousel: boolean
  detailCards: DetailCard[]
  carouselBackground: {
    vimeoId?: string
    gif: IUrl[]
    image: IUrl[]
    videos: {
      posterImage: IUrl[]
      video: IUrl[]
      posterImageUrlS3: string
    }[]
    imageUrlS3: string
    mobileImage: IUrl[]
    mobileImageS3Url: string
    mobileVideos: {
      posterImage: IUrl[]
      video: IUrl[]
      posterImageUrlS3: string
    }[]
  }[]
  modalBackground?: {
    url: string
  }
  eventModalContent: EventModalContentType
}

interface MediaDetails {
  activeBackgroundVideo: IUrl | undefined
  activeBackgroundPosterImage: string | undefined
  activeBackgroundImage: string
  activeBackgroundGif: string
}

const TileCarousel = ({
  items,
  loopCarousel = true,
  detailCards,
  carouselBackground,
  eventModalContent
}: TileCarouselProps) => {
  const sliderRef = useRef(null)
  const [detailsCardHeight, setDetailsCardHeight] = useState<number>(0)
  const [activeTileIndex, setActiveTileIndex] = useState<number>(0)
  const [isVideoPaused, setIsVideoPaused] = useState<boolean>(false)
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const { isBreakpoint: isBelowBreakpointLg } = useWindowSize('lg', '<')
  const isSmartphoneLandscape = useIsSmartphoneLandscape() || isMobileOnly
  const autoplaySpeed = 7000
  const [playButtonActive, setPlayButtonActive] = useState(false)
  const [activeBackgroundImage, setActiveBackgroundImage] = useState<string>('')
  const [activeBackgroundGif, setActiveBackgroundGif] = useState<string>('')
  const [activeBackgroundVideo, setActiveBackgroundVideo] = useState<IUrl | undefined>()
  const [mediaDetails, setMediaDetails] = useState<MediaDetails[]>([])
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [ariaAttributes, setAriaAttributes] = useState<{
    labelledby?: string
    describedby?: string
  }>({})
  const [isImgLoaded, setIsImgLoaded] = useState(false)
  const [preloadImage, setPreloadImage] = useState<string | null>(null)
  const { asPath } = useRouter()

  useEffect(() => {
    if (isModalOpen && eventModalContent?.modalBackground?.url) {
      preloadImageByUrl(eventModalContent.modalBackground.url)
        .then(src => {
          setPreloadImage(src as string)
        })
        .catch(() => {
          setIsImgLoaded(false)
        })
    }
  }, [isModalOpen, eventModalContent])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const modalClosedAt = localStorage.getItem('modalClosedAt')
      const now = new Date().getTime()

      if (modalClosedAt) {
        const elapsed = now - parseInt(modalClosedAt, 10)

        if (elapsed < TWENTY_FOUR_HOURS_IN_MS) {
          setIsModalOpen(false)
          localStorage.setItem('isModalOpen', 'false')
          return
        } else {
          localStorage.removeItem('modalClosedAt')
        }
      }

      const storedIsModalOpen = localStorage.getItem('isModalOpen')
      if (storedIsModalOpen === 'true') {
        setIsModalOpen(true)
      } else {
        const timer = setTimeout(() => {
          setIsModalOpen(true)
        }, 5000)

        return () => clearTimeout(timer)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('isModalOpen', isModalOpen.toString())
    }
  }, [isModalOpen])

  const handleModalClose = () => {
    if (typeof window !== 'undefined') {
      const now = new Date().getTime()
      localStorage.setItem('modalClosedAt', now.toString())
      localStorage.setItem('isModalOpen', 'false')
    }
    setIsModalOpen(false)
  }

  const ArrowCustom = (props: { direction: string }) => {
    const { direction } = props
    return (
      <div
        className={cx([styles['slick-arrow-custom'], styles[`slick-arrow-custom--${direction}`]])}
        onClick={direction === 'next' ? nextSlide : prevSlide}
      >
        <FontAwesomeIcon size='lg' icon={direction === 'next' ? faChevronRight : faChevronLeft} />
      </div>
    )
  }

  const nextSlide = () => {
    if (sliderRef.current) {
      // @ts-ignore
      sliderRef.current.slickNext()
    }
  }

  const prevSlide = () => {
    if (sliderRef.current) {
      // @ts-ignore
      sliderRef.current.slickPrev()
    }
  }

  const slidePlay = () => {
    if (sliderRef.current) {
      // @ts-ignore
      sliderRef.current.slickPlay()
    }
  }

  const slidePause = () => {
    if (sliderRef.current) {
      // @ts-ignore
      sliderRef.current.slickPause()
    }
  }

  const carouselSettings = {
    customPaging: (i: number) => {
      if ((isBelowBreakpointMd || isSmartphoneLandscape) && typeof window !== 'undefined') {
        return <button>{i + 1}</button>
      } else {
        const item = items[i]

        const handleMouseEnter = () => {
          if (!playButtonActive) {
            setIsVideoPaused(true)
          }
        }
        const handleMouseLeave = () => {
          if (!playButtonActive) {
            setIsVideoPaused(false)
          }
        }

        return (
          <div
            key={i}
            className={cx(styles['carousel-nav__item'], {
              [styles['is-active']]: activeTileIndex === i
            })}
            onMouseEnter={() => handleMouseEnter()}
            onMouseLeave={() => handleMouseLeave()}
          >
            <div
              className={styles['item__image-text-wrapper']}
              onClick={() => {
                fireAnalyticsEvent(null, asPath, {
                  event: 'thumbnail_click',
                  clickTracking: `hp_hero:thumbnail-cta:${item.text}`,
                  clickType: 'cta',
                  linkModule: 'hp_hero',
                  linkPosition: getGtmLinkPosition(items, i)
                })
              }}
            >
              {item?.imageUrlS3 && (
                <Image
                  src={constructS3Url(item.imageUrlS3)}
                  alt={item.text}
                  className={styles['item__image']}
                  fill
                  quality={NEXT_IMAGE_QUALITY}
                  style={{
                    objectFit: 'cover'
                  }}
                  sizes='auto'
                />
              )}

              {item?.text && (
                <div className={styles['tile-text']}>
                  <Heading level={4}>{item.text}</Heading>
                </div>
              )}
            </div>
          </div>
        )
      }
    },
    arrows: !isBelowBreakpointMd && !isSmartphoneLandscape,
    dots: true,
    // Changed to fade animation as it should consume data
    // Slide style animation dynamically copies slides, making more video requests
    fade: true,
    cssEase: 'linear',
    dotsClass:
      isBelowBreakpointMd || isSmartphoneLandscape
        ? cx('slick-dots', [styles['carousel-nav'], styles['carousel-nav--mobile']])
        : cx([styles['carousel-nav'], styles['carousel-nav--desktop']]),
    autoplay: true,
    infinite: loopCarousel,
    lazyLoad: 'ondemand',
    speed: 500,
    autoplaySpeed: autoplaySpeed,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current: number, next: number) => setActiveTileIndex(next),
    prevArrow: <ArrowCustom direction='prev' />,
    nextArrow: <ArrowCustom direction='next' />
  }

  useEffect(() => (isVideoPaused ? slidePause() : slidePlay()), [isVideoPaused])

  const togglePlay = () => {
    setIsVideoPaused(false)
    setPlayButtonActive(false)
  }

  const togglePause = () => {
    setIsVideoPaused(true)
    setPlayButtonActive(true)
  }

  useEffect(() => {
    if (!carouselBackground) return

    const loadMediaDetails = async () => {
      const details = await Promise.all(
        carouselBackground.map(async ({ vimeoId, gif, videos, imageUrlS3, mobileVideos, mobileImageS3Url }) => {
          let videoSource

          if (isBelowBreakpointLg) {
            if (mobileVideos.length > 0 && mobileVideos[0].video) {
              videoSource = mobileVideos[0]
            } else {
              videoSource = videos[0]
            }
          } else {
            if (videos.length > 0 && videos[0].video) {
              videoSource = videos[0]
            }
          }
          let activeBackgroundVideo = videoSource?.video[0]
          let activeBackgroundPosterImage = videoSource?.posterImageUrlS3
          let activeBackgroundImage = constructS3Url(imageUrlS3)
          if (isBelowBreakpointLg && mobileImageS3Url) {
            activeBackgroundImage = constructS3Url(mobileImageS3Url)
          }

          const activeBackgroundGif = gif?.length > 0 ? gif[0].url : ''

          if (vimeoId) {
            // Gracefully fall back to existing video sources or static media
            // activeBackgroundVideo and activeBackgroundPosterImage will retain their original values
            try {
              const vimeoData = await fetchVimeoVideo(vimeoId)
              const vimeoVideo = getVimeoVideoByRendition('1080p', vimeoData)
              if (vimeoVideo) activeBackgroundVideo = { url: vimeoVideo }
              const vimeoPosterImage = getVimeoPictureByResolution('1920x1080', vimeoData)
              if (vimeoPosterImage) activeBackgroundPosterImage = vimeoPosterImage
            } catch {
              // Silently fail - fallback to existing video sources
              console.warn(`Failed to fetch Vimeo video for ID: ${vimeoId}. Using existing video sources.`)
            }
          }

          return {
            activeBackgroundVideo,
            activeBackgroundPosterImage,
            activeBackgroundImage,
            activeBackgroundGif
          }
        })
      )
      setMediaDetails(details)
      const activeTile = details[activeTileIndex]
      if (activeTile) {
        setActiveBackgroundVideo(activeTile.activeBackgroundVideo)
        setActiveBackgroundImage(activeTile.activeBackgroundImage)
        setActiveBackgroundGif(activeTile.activeBackgroundGif)
      }
    }

    loadMediaDetails()
  }, [activeTileIndex, carouselBackground, isBelowBreakpointLg])

  return (
    <div className={styles['home-page-container']}>
      <Slider {...carouselSettings} ref={sliderRef}>
        {mediaDetails?.map(({ activeBackgroundPosterImage, activeBackgroundVideo }, index) => {
          return (
            <div className={styles['slide']} key={index}>
              <div style={{ opacity: '60%' }}>
                {activeBackgroundVideo && activeBackgroundPosterImage ? (
                  <Suspense fallback={<>Loading...</>}>
                    <CarouselVideo
                      autoPlay={activeTileIndex == index && !isVideoPaused}
                      videoLoop={true}
                      posterImage={
                        activeBackgroundPosterImage ? { url: constructS3Url(activeBackgroundPosterImage) } : { url: '' }
                      }
                      backgroundVideo={activeBackgroundVideo}
                      isPaused={activeTileIndex !== index || isVideoPaused}
                      detailsCardHeight={detailsCardHeight}
                      shouldPreload={activeTileIndex == index || activeTileIndex + 1 == index}
                    />
                  </Suspense>
                ) : activeBackgroundImage || activeBackgroundGif ? (
                  <Image
                    src={activeBackgroundImage ? activeBackgroundImage : activeBackgroundGif}
                    fill
                    style={{
                      objectFit: 'cover'
                    }}
                    alt={''} // @TODO: add alt value from cms
                  />
                ) : null}
              </div>
            </div>
          )
        })}
      </Slider>
      <DetailsCard
        description={detailCards[activeTileIndex]?.description || { html: '' }}
        headline={detailCards[activeTileIndex]?.headline}
        eyebrow={detailCards[activeTileIndex]?.eyebrow}
        cta={detailCards[activeTileIndex]?.cta}
        isDescriptionVisibleDesktopOrTablet={detailCards[activeTileIndex]?.isDescriptionVisibleDesktopOrTablet}
        isDescriptionVisibleMobile={detailCards[activeTileIndex]?.isDescriptionVisibleMobile}
        eyebrowColor={detailCards[activeTileIndex]?.eyebrowColor}
        setDetailsCardHeight={setDetailsCardHeight}
        tileIndex={activeTileIndex}
      />
      {eventModalContent && isImgLoaded && preloadImage && (
        <PortalModal
          isOpen={isModalOpen}
          handleModalClose={handleModalClose}
          modalBackgroundUrl={preloadImage}
          type='event'
          ariaLabelledBy={ariaAttributes.labelledby}
          ariaDescribedBy={ariaAttributes.describedby}
        >
          <EventModalContent
            description={eventModalContent.description}
            disclaimer={eventModalContent.disclaimer}
            statusBar={eventModalContent.statusBar}
            title={eventModalContent.title}
            statusBarColor={eventModalContent.statusBarColor}
            buttonGroup={eventModalContent.buttonGroup}
            setAriaAttributes={setAriaAttributes}
          />
        </PortalModal>
      )}

      {!isBelowBreakpointMd && !isSmartphoneLandscape && activeBackgroundVideo && (
        <div className={cx([styles['play-pause-container']])} onClick={playButtonActive ? togglePlay : togglePause}>
          {playButtonActive ? <PlayButton /> : <PauseButton />}
        </div>
      )}
    </div>
  )
}

export default TileCarousel
